import {WikiPageListPage} from "./pages/admin/employee/wiki";
import {SECTION_COMPLIANCE, WikiPageEditPage} from "./pages/admin/employee/wiki/form";
import {NoteAlt as WikiIcon} from "@mui/icons-material";
import {Navigate, Outlet, Route, useLocation} from "react-router-dom";
import * as React from "react";
import {
	Bookmarks,
	ContentPaste,
	Dashboard,
	Description,
	FactCheck,
	AssignmentInd,
	EventBusy,
	EventNote,
	HourglassBottom,
	Timer,
	HelpCenter,
	Link as LinkIcon,
	ReportProblem,
	School,
	Star,
	VerifiedUser,
} from "@mui/icons-material";
import SideBarItem from "./sidebar_item";
import {COMPLIANCE, useComplianceAccess} from "./pages/admin/compliance/access";
import {ComplianceDashboardPage} from "./pages/admin/compliance";
import {QualityFindingListPage} from "./pages/admin/compliance/finding";
import {QualityFindingEditPage} from "./pages/admin/compliance/finding/form";
import {QualityActionEditPage} from "./pages/admin/compliance/action/form";
import {AccessReviewListPage} from "./pages/admin/compliance/access_review";
import {AccessReviewEditPage} from "./pages/admin/compliance/access_review/form";
import {RoleAssignmentPage} from "./pages/admin/compliance/role_assignment";
import {SupportListPage} from "./pages/admin/compliance/support";
import {SupportEditPage} from "./pages/admin/compliance/support/form";
import {SupportLinkListPage} from "./pages/admin/compliance/supportlink";
import {SupportLinkEditPage} from "./pages/admin/compliance/supportlink/form";
import {SupportPointListPage} from "./pages/admin/compliance/supportpoint";
import {SupportPointEditPage} from "./pages/admin/compliance/supportpoint/form";
import {SupportResultListPage} from "./pages/admin/compliance/supportresult";
import {SupportResultEditPage} from "./pages/admin/compliance/supportresult/form";
import {DocumentListPage} from "./pages/admin/compliance/document";
import {DocumentEditPage} from "./pages/admin/compliance/document/form";
import {DocumentTopicListPage} from "./pages/admin/compliance/documenttopic";
import {DocumentTopicEditPage} from "./pages/admin/compliance/documenttopic/form";
import {RequiredDocumentsPage} from "./pages/admin/compliance/required_documents";
import {ExpiredDocumentsPage} from "./pages/admin/compliance/expired_documents";
import {PersonDocumentRetentionListPage} from "./pages/admin/compliance/retention";
import {PersonDocumentRetentionEditPage} from "./pages/admin/compliance/retention/form";
import {ComplianceCalendarPage} from "./pages/admin/compliance/calendar";
import {CompanyExpiryListPage} from "./pages/admin/compliance/expiry";
import {CompanyExpiryEditPage} from "./pages/admin/compliance/expiry/form";

/**
 * Compliance (#13899): el sistema de gestión (ISO 9001, ISO/IEC 27001 y ENS) en su propio módulo,
 * con el rol ADMIN_COMPLIANCE. Reúne lo que estaba en Configuración (no conformidades, revisión de
 * accesos, roles) y en RR. HH. (valoración de clientes, documentos de empresa). Los documentos los
 * sigue abriendo RR. HH.; la formación se queda en RR. HH. y aquí solo se enlaza.
 */
export {COMPLIANCE};

/** Lleva una dirección antigua a la nueva conservando lo que venía detrás (un id, «new», la query). */
const MovedTo = ({from, to}: { from: string, to: string }) => {
	const location = useLocation();
	const rest = location.pathname.startsWith(from) ? location.pathname.substring(from.length) : "";
	return <Navigate to={to + rest + location.search} replace/>;
};

/** Rutas que se fueron al módulo: los enlaces guardados y los de los correos siguen sirviendo. */
const MOVED: { from: string, to: string }[] = [
	{from: "/app/admin/config/quality", to: COMPLIANCE},
	{from: "/app/admin/employee/documenttopic", to: `${COMPLIANCE}/documenttopic`},
	{from: "/app/admin/employee/document", to: `${COMPLIANCE}/document`},
	{from: "/app/admin/employee/supportlink", to: `${COMPLIANCE}/supportlink`},
	{from: "/app/admin/employee/supportpoint", to: `${COMPLIANCE}/supportpoint`},
	{from: "/app/admin/employee/supportresult", to: `${COMPLIANCE}/supportresult`},
	{from: "/app/admin/employee/support", to: `${COMPLIANCE}/support`},
];

export const ComplianceRedirects = () => MOVED.map(m => (
	<Route key={m.from} path={`${m.from}/*`} element={<MovedTo from={m.from} to={m.to}/>}/>
));

const AdminComplianceRoutes = () => {
	return (
		<Route path={COMPLIANCE} element={<Outlet/>}>
			<Route index element={<ComplianceDashboardPage/>}/>

			<Route path={`${COMPLIANCE}/finding`} element={<QualityFindingListPage/>}/>
			<Route path={`${COMPLIANCE}/finding/:id`} element={<QualityFindingEditPage/>}/>
			<Route path={`${COMPLIANCE}/finding/new`} element={<QualityFindingEditPage/>}/>
			<Route path={`${COMPLIANCE}/action/:id`} element={<QualityActionEditPage/>}/>
			<Route path={`${COMPLIANCE}/action/new`} element={<QualityActionEditPage/>}/>

			<Route path={`${COMPLIANCE}/access-review`} element={<AccessReviewListPage/>}/>
			<Route path={`${COMPLIANCE}/access-review/:id`} element={<AccessReviewEditPage/>}/>
			<Route path={`${COMPLIANCE}/access-review/new`} element={<AccessReviewEditPage/>}/>
			<Route path={`${COMPLIANCE}/role-assignment`} element={<RoleAssignmentPage/>}/>
			<Route path={`${COMPLIANCE}/required-documents`} element={<RequiredDocumentsPage/>}/>
			<Route path={`${COMPLIANCE}/calendar`} element={<ComplianceCalendarPage/>}/>
			<Route path={`${COMPLIANCE}/expiry`} element={<CompanyExpiryListPage/>}/>
			<Route path={`${COMPLIANCE}/expiry/:id`} element={<CompanyExpiryEditPage/>}/>
			<Route path={`${COMPLIANCE}/expiry/new`} element={<CompanyExpiryEditPage/>}/>
			<Route path={`${COMPLIANCE}/expired-documents`} element={<ExpiredDocumentsPage/>}/>
			<Route path={`${COMPLIANCE}/retention`} element={<PersonDocumentRetentionListPage/>}/>
			<Route path={`${COMPLIANCE}/retention/:id`} element={<PersonDocumentRetentionEditPage/>}/>
			<Route path={`${COMPLIANCE}/retention/new`} element={<PersonDocumentRetentionEditPage/>}/>

			<Route path={`${COMPLIANCE}/support`} element={<SupportListPage/>}/>
			<Route path={`${COMPLIANCE}/support/:id`} element={<SupportEditPage/>}/>
			<Route path={`${COMPLIANCE}/support/new`} element={<SupportEditPage/>}/>
			<Route path={`${COMPLIANCE}/supportlink`} element={<SupportLinkListPage/>}/>
			<Route path={`${COMPLIANCE}/supportlink/:id`} element={<SupportLinkEditPage/>}/>
			<Route path={`${COMPLIANCE}/supportlink/new`} element={<SupportLinkEditPage/>}/>
			<Route path={`${COMPLIANCE}/supportpoint`} element={<SupportPointListPage/>}/>
			<Route path={`${COMPLIANCE}/supportpoint/:id`} element={<SupportPointEditPage/>}/>
			<Route path={`${COMPLIANCE}/supportpoint/new`} element={<SupportPointEditPage/>}/>
			<Route path={`${COMPLIANCE}/supportresult`} element={<SupportResultListPage/>}/>
			<Route path={`${COMPLIANCE}/supportresult/:id`} element={<SupportResultEditPage/>}/>
			<Route path={`${COMPLIANCE}/supportresult/new`} element={<SupportResultEditPage/>}/>

			<Route path={`${COMPLIANCE}/document`} element={<DocumentListPage/>}/>
			<Route path={`${COMPLIANCE}/document/:id`} element={<DocumentEditPage/>}/>
			<Route path={`${COMPLIANCE}/document/new`} element={<DocumentEditPage/>}/>
			<Route path={`${COMPLIANCE}/documenttopic`} element={<DocumentTopicListPage/>}/>
			<Route path={`${COMPLIANCE}/documenttopic/:id`} element={<DocumentTopicEditPage/>}/>
			<Route path={`${COMPLIANCE}/documenttopic/new`} element={<DocumentTopicEditPage/>}/>
			<Route path="/app/admin/compliance/wiki" element={<WikiPageListPage section={SECTION_COMPLIANCE}/>}/>
			<Route path="/app/admin/compliance/wiki/:id" element={<WikiPageEditPage section={SECTION_COMPLIANCE}/>}/>
			<Route path="/app/admin/compliance/wiki/new" element={<WikiPageEditPage section={SECTION_COMPLIANCE}/>}/>
		</Route>
	);
};

export default AdminComplianceRoutes;

export const AdminComplianceMenu = () => {
	const access = useComplianceAccess();
	return (
		<>
			{access.full && (
				<>
					<SideBarItem href={COMPLIANCE} label={"Indicadores"} icon={<Dashboard/>}/>
					<SideBarItem href={`${COMPLIANCE}/finding`} label={"No conformidades e incidentes"} icon={<ReportProblem/>}/>
					<SideBarItem href={`${COMPLIANCE}/access-review`} label={"Revisión de accesos"} icon={<FactCheck/>}/>
					<SideBarItem href={`${COMPLIANCE}/role-assignment`} label={"Roles del sistema de gestión"} icon={<VerifiedUser/>}/>
					<SideBarItem href={`${COMPLIANCE}/calendar`} label={"Calendario del sistema de gestión"} icon={<EventNote/>}/>
					<SideBarItem href={`${COMPLIANCE}/expiry`} label={"Caducidades de la empresa"} icon={<HourglassBottom/>}/>
					<SideBarItem href={`${COMPLIANCE}/required-documents`} label={"Documentación de la plantilla"} icon={<AssignmentInd/>}/>
					<SideBarItem href={`${COMPLIANCE}/expired-documents`} label={"Documentos fuera de plazo"} icon={<EventBusy/>}/>
					<SideBarItem href={`${COMPLIANCE}/retention`} label={"Plazos de conservación"} icon={<Timer/>}/>
				</>
			)}
			<SideBarItem href={`${COMPLIANCE}/document`} label={"Documentos de empresa"} icon={<Description/>}/>
			<SideBarItem href={`${COMPLIANCE}/documenttopic`} label={"Temas de documentos"} icon={<Bookmarks/>}/>
			{access.full && (
				<>
					<SideBarItem href={`${COMPLIANCE}/support`} label={"Valoración de clientes"} icon={<Star/>}/>
					<SideBarItem href={`${COMPLIANCE}/supportlink`} label={"Enlaces de valoración"} icon={<LinkIcon/>}/>
					<SideBarItem href={`${COMPLIANCE}/supportpoint`} label={"Preguntas de valoración"} icon={<HelpCenter/>}/>
					<SideBarItem href={`${COMPLIANCE}/supportresult`} label={"Respuestas de valoración"} icon={<ContentPaste/>}/>
				</>
			)}
			{access.full && <SideBarItem href={`${COMPLIANCE}/wiki`} label={"Wiki"} icon={<WikiIcon/>}/>}
			{access.hr && (
				<SideBarItem href={"/app/admin/employee/training"} label={"Formación (en RR. HH.)"} icon={<School/>}/>
			)}
		</>
	);
};
